import { Link } from "react-router-dom";
import { ArrowRight, Clock, DollarSign, FileCheck, GraduationCap, Loader2, MapPin } from "lucide-react";
import { useUpcomingCourses } from "@/hooks/useUpcomingCourses";

const blank = "-";

export default function Course() {
  const { courses, loading, error } = useUpcomingCourses();
  const course = courses[0];
  const value = (name, fallback = blank) =>
    course?.[name] ?? course?.[`${name.charAt(0).toLowerCase()}${name.slice(1)}`] ?? fallback;

  if (loading) {
    return <div className="flex min-h-80 items-center justify-center"><Loader2 className="animate-spin text-orange-500" /></div>;
  }

  if (error || !course) {
    return <div className="mx-auto max-w-7xl px-4 py-16 text-slate-600">{error || "Course details are currently unavailable."}</div>;
  }

  const fee = Number(value("CourseFee", value("Price", 0)) || 0);
  const campus = [value("CampusName", ""), value("Suburb", "")].filter(Boolean).join(" | ");
  const scheduleDate = value("ScheduleDate", "");
  const facts = [
    { icon: Clock, label: "Duration", text: value("Duration") },
    { icon: MapPin, label: "Location", text: campus || blank },
    { icon: DollarSign, label: "Fee", text: `$${fee.toFixed(2)} inc GST` },
    { icon: FileCheck, label: "Availability", text: `${value("AvailableSeats", 0)} seats available` },
  ];

  return (
    <div>
      <section className="bg-[#0A2540] py-16 text-white lg:py-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-orange-400">Course Details</div>
          <h1 className="font-display text-4xl font-extrabold leading-tight lg:text-6xl">{value("CourseCode")}</h1>
          <p className="mt-3 max-w-3xl text-xl text-slate-300">{value("CourseName")}</p>
          <p className="mt-4 max-w-3xl text-slate-300">{value("CourseDescription", "")}</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
            {facts.map(({ icon: Icon, label, text }) => <div key={label} className="rounded-lg border border-white/10 bg-white/5 p-4"><Icon className="mb-2 h-5 w-5 text-orange-400" /><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div><div className="mt-1 text-sm font-semibold">{text}</div></div>)}
          </div>
          <Link to="/calendar" className="wc-btn-primary mt-8" data-testid="course-book-cta">Book a class <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
      <section className="mx-auto grid max-w-7xl gap-8 px-4 py-16 lg:grid-cols-3">
        <div className="lg:col-span-2"><div className="wc-card"><div className="mb-4 flex items-center gap-2"><GraduationCap className="h-5 w-5 text-orange-500" /><h2 className="font-display text-2xl font-extrabold text-[#0A2540]">Course information</h2></div><dl className="grid gap-4 sm:grid-cols-2"><div><dt className="text-xs uppercase text-slate-500">Delivery mode</dt><dd>{value("DeliveryMode")}</dd></div><div><dt className="text-xs uppercase text-slate-500">Trainer</dt><dd>{value("TrainerName")}</dd></div><div><dt className="text-xs uppercase text-slate-500">Schedule</dt><dd>{scheduleDate ? new Date(scheduleDate).toLocaleDateString("en-AU") : blank}</dd></div><div><dt className="text-xs uppercase text-slate-500">Campus</dt><dd>{campus || blank}</dd></div></dl></div></div>
        <aside><div className="wc-card border-none bg-[#0A2540] text-white"><div className="text-xs uppercase tracking-wide text-orange-300">Course fee</div><div className="mt-1 font-display text-4xl font-extrabold">${fee.toFixed(2)}</div><div className="mt-2 text-sm text-slate-300">{value("DeliveryMode")} | {value("Duration")}</div><Link to="/calendar" className="wc-btn-primary mt-5 block justify-center text-center">Book now</Link></div></aside>
      </section>
    </div>
  );
}
